export const blogPosts = [
  /* ============================================================
     ARTÍCULO 1 — EXTENDIDO Y OPTIMIZADO
  ============================================================ */
  {
    id: 1,
    slug: "difusion-industrial-sergen",
    title: "Cómo las industrias pueden optimizar su demanda eléctrica",
    excerpt:
      "Descubre cómo las industrias pueden reducir hasta 36% de su facturación mediante control de demanda, análisis predictivo y programación operativa.",
    category: "Control de Demanda",
    date: "2025-08-30",
    image: "/lovable-uploads/Difusion-Industria.jpg",

    content: `
      <h2>La gestión energética moderna: por qué la demanda importa más que el consumo</h2>

      <p>
        En un entorno industrial donde los costos energéticos representan entre 
        <strong>15% y 40% de los gastos operativos</strong>, cualquier mejora de eficiencia 
        impacta directamente en la rentabilidad. Sin embargo, el verdadero origen de los 
        sobrecostos no suele ser el consumo total de energía, sino los 
        <strong>picos de demanda no controlados</strong>.
      </p>

      <p>
        Estos picos obligan a contratar potencias superiores a las necesarias y generan 
        penalizaciones que inflan la factura sin aportar valor productivo.
      </p>

      <blockquote>
        <strong>
          Gracias al IoT, big data y modelos predictivos modernos, hoy es posible anticipar 
          los picos con horas —o incluso días— de antelación, evitando penalidades sin 
          afectar la producción.
        </strong>
      </blockquote>

      <p>
        Estudios de la Agencia Internacional de la Energía y experiencias reales en España 
        y Latinoamérica demuestran reducciones entre <strong>20% y 36%</strong> cuando se 
        aplica control predictivo de demanda.
      </p>

      <h3>El problema real: impacto económico de los picos</h3>

      <p>En las tarifas industriales, la factura eléctrica incluye:</p>

      <ul>
        <li><strong>Término de potencia:</strong> se paga la potencia contratada, se use o no.</li>
        <li><strong>Término de energía:</strong> kWh consumidos.</li>
        <li><strong>Penalizaciones:</strong> por excesos de demanda o energía reactiva.</li>
      </ul>

      <p>
        Cuando ocurre un pico —por ejemplo, arranques simultáneos de equipos— la eléctrica:
      </p>

      <ul>
        <li>Registra ese máximo cada 15 minutos.</li>
        <li>Aumenta la potencia facturada durante el trimestre.</li>
        <li>Aplica cargos por bajo factor de potencia.</li>
      </ul>

      <p>
        Resultado: una planta que realmente necesita <strong>800 kW</strong> puede terminar 
        pagando como si requiriera <strong>1.400 kW</strong>, solo por algunos picos mensuales.
      </p>

      <h3>Acciones con retorno inmediato (ROI &lt; 6 meses)</h3>

      <h4>1. Reprogramación inteligente de procesos</h4>
      <p>
        Mover procesos no críticos fuera de horas punta.  
        Ejemplo real: una planta metalúrgica redujo un <strong>28%</strong> su término de 
        potencia retrasando 45 minutos dos prensas de 250 kW.
      </p>

      <h4>2. Secuenciación automática de arranques</h4>
      <p>
        Evitar arranques simultáneos mediante PLC/SCADA.  
        Caso real: fábrica de plásticos eliminó picos de 650 kW escalonando 7 motores → 
        <strong>31% de reducción</strong> en potencia facturada.
      </p>

      <h4>3. Alertas predictivas en tiempo real</h4>
      <p>Ejemplo típico:</p>

      <blockquote>
        “Alerta: Riesgo alto de pico a las 10:15 h (89%).  
        Potencia prevista: 1.320 kW.  
        Acción sugerida: pausar bomba B-03 o adelantar parada de compresores.”
      </blockquote>

      <h4>4. Monitoreo granular con IoT</h4>
      <p>
        Permite identificar qué línea o máquina genera el desvío y actuar sin detener toda la planta.
      </p>

      <h4>5. Peak shaving con baterías industriales</h4>
      <p>
        Un sistema BESS de 500 kW / 1 MWh puede eliminar picos y participar en programas de 
        respuesta de demanda.
      </p>

      <h3>Resultados reales (2023–2025)</h3>

      <ul>
        <li><strong>Industria alimentaria (Galicia):</strong> –36% → 380.000 €/año</li>
        <li><strong>Química (Cataluña):</strong> –24% → 290.000 €/año</li>
        <li><strong>Automoción (México):</strong> –31% → 420.000 USD/año</li>
        <li><strong>Astillero (Perú):</strong> –29% → 180.000 USD/año</li>
      </ul>

      <h3>La solución SERGEN</h3>

      <p>
        SERGEN integra sensores IoT, modelos predictivos con más del 92% de precisión, 
        dashboard en tiempo real y alertas personalizadas según el rol de cada responsable.
      </p>

      <p><strong>Cada mañana nuestros clientes reciben:</strong></p>

      <ul>
        <li>Potencia máxima prevista del día</li>
        <li>Acciones recomendadas con impacto en €</li>
        <li>Simulación comparativa de factura</li>
      </ul>

      <h3>Conclusión</h3>

      <p>
        Controlar la demanda eléctrica ya no es opcional: es una ventaja competitiva crítica.  
        Si tu industria paga más de <strong>200.000 €/año</strong> en electricidad, es muy 
        probable que estés dejando entre <strong>40.000 y 120.000 €</strong> anuales por no 
        gestionar adecuadamente tus picos de demanda.
      </p>

      <p>
        Las empresas que adoptan control predictivo convierten su consumo eléctrico en una 
        variable estable, predecible y optimizada —sin afectar producción.
      </p>
    `
  },

  /* ============================================================
     ARTÍCULO 2 (SIN MODIFICAR)
  ============================================================ */
  {
    id: 2,
    slug: "sector-industrial-retos-energia-2025",
    title: "Los principales retos del sector industrial en eficiencia energética para el 2025",
    excerpt:
      "El año 2025 trae nuevos desafíos para las industrias: aumento de costos, presión ambiental y la necesidad urgente de digitalizar la gestión energética.",
    category: "Industria & Operaciones",
    date: "2025-02-12",
    image: "/lovable-uploads/sector-industria.jpg",
    content: `
      <h2>Tendencias energéticas que marcarán el panorama industrial del 2025</h2>
      <p>
        Las industrias enfrentan una transición acelerada hacia la eficiencia energética. El incremento
        de tarifas, nuevas exigencias ambientales y la necesidad de optimizar recursos han colocado el
        tema en el centro de la agenda operativa.
      </p>

      <h3>1. Digitalización del consumo</h3>
      <p>
        La digitalización permite analizar el uso energético en tiempo real. Plataformas inteligentes 
        entregan visibilidad por turno, por máquina y por área, facilitando decisiones basadas en datos.
      </p>

      <h3>2. Mantenimiento predictivo</h3>
      <p>
        La ineficiencia de los equipos incrementa la factura eléctrica hasta en un 18%. 
        El mantenimiento predictivo previene fallas, reduce sobrecargas y prolonga la vida útil de los activos.
      </p>

      <h3>3. Normativas ambientales y sostenibilidad</h3>
      <p>
        Certificaciones como ISO 50001 se han vuelto un estándar indispensable para competir globalmente.
      </p>

      <h3>¿Qué deben priorizar las industrias en 2025?</h3>
      <ul>
        <li>Medición y análisis de consumos por procesos.</li>
        <li>Automatización de reportes energéticos.</li>
        <li>Implementación de políticas internas de eficiencia.</li>
        <li>Integración de tecnologías con enfoque ESG.</li>
      </ul>
    `
  },

  /* ============================================================
     ARTÍCULO 3 (SIN MODIFICAR)
  ============================================================ */
  {
    id: 3,
    slug: "como-leer-tu-factura-electrica-sergen",
    title: "Cómo leer correctamente tu factura eléctrica y detectar gastos ocultos",
    excerpt:
      "Muchas industrias pagan más de lo necesario por desconocer los conceptos de su factura: potencia excedida, energía reactiva, penalidades y consumos mal clasificados.",
    category: "Eficiencia Energética",
    date: "2025-02-15",
    image: "/lovable-uploads/facturas-existen.jpg",
    content: `
      <h2>Comprende tu factura eléctrica y reduce gastos innecesarios</h2>

      <p>
        Más del 60% de las empresas no revisan a detalle los conceptos facturados, lo que genera penalizaciones 
        y sobrecostos evitables.
      </p>

      <h3>Conceptos que debes revisar</h3>
      <ul>
        <li><strong>Potencia contratada vs. potencia máxima:</strong> si excedes, la empresa eléctrica penaliza.</li>
        <li><strong>Energía activa:</strong> kWh consumidos.</li>
        <li><strong>Energía reactiva:</strong> genera sobrecargos si supera límites establecidos.</li>
        <li><strong>Horas punta:</strong> periodos de tarifa elevada que impactan fuertemente en la factura.</li>
      </ul>

      <h3>¿Cómo detectar gastos ocultos?</h3>
      <p>
        Auditorías mensuales permiten identificar errores en la facturación, consumos irregulares y oportunidades de renegociación.
      </p>

      <h3>Recomendaciones prácticas</h3>
      <ul>
        <li>Evitar arranques simultáneos.</li>
        <li>Corregir energía reactiva con bancos de capacitores.</li>
        <li>Evitar incrementos durante horas punta.</li>
        <li>Confirmar que la categoría tarifaria es la correcta.</li>
      </ul>

      <p>
        En SERGEN ayudamos a interpretar consumos, detectar oportunidades y reducir costos sin afectar la producción.
      </p>
    `
  }
];
